<template>
  <config-base
    class="opc-aic-config"
    @view-details="showPlanDetailDialog"
    :config-plan-tile-status="configPlanTileStatus"
  >
    <template #config>
      <div>
        <div class="opc-aic-config__my-notes-desc" v-if="shouldMyNotes">{{ $t('billing.my_notes.desc') }}</div>
        <div class="opc-aic-config__item-label">{{ $t('billing.config.select_number_users') }}</div>
        <input-number-with-popover
          ref="userInput"
          size="large"
          controls-position="both"
          class="top-gap8"
          v-model="licenseNumber"
          :min="minLicenses"
          :max="maxLicenses"
          :decreaseTips="decreaseTips"
          :label="`${title} select the number of users`"
          :dataTracking="{ buyInteraction: 'number of users', planName: planTrackingName }"
          :data-testid="`checkout-input-license-${selectedPlan ? selectedPlan.plan_code : ''}`"
          @change="updatePlanLicense"
        />
        <zm-alert v-if="mx_downSellLicenseErrorMessage" type="info" class="top-gap16 opc-aic-config__license-alert">
          {{ mx_downSellLicenseErrorMessage }}
        </zm-alert>
        <config-cycle
          v-model="billingCycle"
          :showMonthlyPriceFromCycle="true"
          :billing-cycle-list="billingCycleList"
          @switchCycleAction="updateBillingCycle"
          :unit="unit"
        />
        
        <zm-banner class="top-gap16" v-if="shouldMyNotes" :content="$t('billing.my_notes.declaration')" type="info" />
        <zm-banner
          v-else-if="enableCAICPlanRebranding"
          class="top-gap16"
          :content="$t('billing.zoom_ai_companion.rebranding_notice')"
          type="info"
        />

        <preview-payment class="top-gap24" />
      </div>
    </template>
  </config-base>
</template>

<script>
import ConfigBase from '@/components/config-base.vue';
import commonMixin from './mixin';
import plansMixin from '../mx/plan';
import addonMixin from '../mx/addon';
import { planTypes } from '@/common/constants';
import { planUnitMap } from '@/common/product-unit';
import planDetailsDialogMixin from './utils/plan-details-dialog';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { plansof } from '@/utils/chain';
import { translateToEn } from '@/plugins/i18n';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';
import ConfigCycle from '@/components/service/config-billing-cycle/index.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { enableCAICPlanRebranding } from '@/plans/ZOOM_CUSTOM_AI_COMPANION/utils.js';

const ConfigBaseComp = {
  name: 'ZoomAiCompanionBase',
  extends: ConfigBase,
  mixins: [commonMixin],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: true,
      unit: planUnitMap.user,
    };
  },
};

export default {
  name: 'ZoomAiCompanionConfig',
  mixins: [commonMixin, plansMixin, addonMixin, planDetailsDialogMixin],
  components: {
    ConfigBase: ConfigBaseComp,
    InputNumberWithPopover,
    ConfigCycle,
    PreviewPayment,
  },
  data() {
    return {
      licenseNumber: 1,
      billingCycle: '',
      unit: planUnitMap.user,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'currentEditPlan', 'basicConfig']),
    minLicenses() {
      if (this.disableDownSell || !this.isStandardPrice) {
        return this.previousQuantity;
      }
      return this.selectedPlan.min;
    },
    disableDownSell() {
      // Check account MRR > 500?
      return this.basicConfig.downSellData.disableDownSell;
    },
    previousQuantity() {
      return this.currentEditPlan?.cartInfo?.selected_number ?? this.selectedPlan.min;
    },
    decreaseTips() {
      if (this.licenseNumber === this.previousQuantity) {
        if (this.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }
        if (this.forNonStandardPriceTips) {
          return this.forNonStandardPriceTips;
        }
      }
      return '';
    },
    maxLicenses() {
      if (this.$context.is.edit) {
        return 9999;
      }
      return 99;
    },
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
    planTrackingName() {
      return translateToEn(this.selectedPlan?.planNameKey);
    },
    planType() {
      return planTypes.ZM_AI_COMPANION;
    },
    title() {
      return enableCAICPlanRebranding
        ? this.$t('billing.zoom_ai_companion.rebranding_title')
        : this.$t('billing.zoom_ai_companion.title');
    },
    enableCAICPlanRebranding() {
      return enableCAICPlanRebranding;
    },
    shouldMyNotes() {
      return window.$query?.referrer === 'mynotes' && !this.$context?.is?.edit;
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),
    /**
     * update license number
     * @param {number} newLicense
     */
    updatePlanLicense(newLicense) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newLicense },
      });
      this.mx_validDownSellLicense(newLicense);
    },
    /**
     * update billing cycle
     * @param {object} oldPlan
     * @param {object} newPlan
     */
    async updateBillingCycle(oldPlan, newPlan) {
      this.$tracking.buyInteraction('subscription cycle', `${this.planTrackingName} - ${newPlan.billing_cycle}`);
      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        { ...newPlan, cartInfo: { selected_number: this.licenseNumber } },
      ]);
    },
  },
  created() {
    if (this.selectedPlan) {
      this.billingCycle = this.selectedPlan.billing_cycle;
      this.licenseNumber = this.selectedPlan.cartInfo?.selected_number;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.licenseNumber);
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/zoom-card.scss';

.opc-aic-config {
  &__my-notes-desc {
    margin-bottom: 10px;
    color: var(--zoom-color-text-neutral, #686F79);
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
  }
  :deep(.top-gap8) {
    margin-top: 8px;
  }
  .top-gap24 {
    margin-top: 24px;
  }

  .top-gap16 {
    margin-top: 16px;
  }

  &__license-alert {
    padding: 16px;
    color: $font-color;
    font-size: 14px;
    line-height: 21px;
    a {
      font-weight: 450;
    }
  }

  &__item-label {
    font-size: 18px;
    font-weight: 700;
    color: $zoom-color-text-stronger-neutral;
    line-height: 27px;
  }
}
</style>
